import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import Seo from "@/components/Seo";
import { orgSchema, breadcrumb } from "@/lib/seo";
import { BLOG_POSTS } from "@/data/blog";
import { ArrowLeft, Clock, ArrowRight } from "@phosphor-icons/react";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { if (!post) navigate("/blog"); }, [post]); // eslint-disable-line

  if (!post) return null;
  const L = post.title[lang] ? lang : (post.body.en ? "en" : "de");
  const fmt = (d) => new Date(d).toLocaleDateString(lang === "de" ? "de-CH" : "en-GB", { day: "numeric", month: "long", year: "numeric" });
  const more = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div data-testid="blog-post-page">
      <Seo
        title={`${post.title[L]} | Airport Transfer Zurich`}
        description={post.excerpt[L]}
        path={`/blog/${post.slug}`}
        image={post.image}
        jsonLd={[
          breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_blog"), path: "/blog" }, { name: post.title[L], path: `/blog/${post.slug}` }]),
          { "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title[L], description: post.excerpt[L], image: post.image, datePublished: post.date, inLanguage: lang, author: { "@type": "Organization", name: "Airport Transfer Zurich" }, publisher: { "@type": "Organization", name: "Airport Transfer Zurich" } },
          ...(post.faq?.[L]?.length ? [{ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: post.faq[L].map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }] : []),
        ]}
      />
      {/* Hero */}
      <div className="relative h-[52vh] min-h-[380px] bg-[#0B1533]">
        <img src={post.image} alt="" className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1533] via-[#0B1533]/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
            <Link to="/blog" className="inline-flex items-center gap-2 font-body text-sm text-white/80 hover:text-white transition-colors duration-200 mb-5"><ArrowLeft size={15} /> {t("back_to_blog")}</Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-body text-xs font-semibold text-white bg-[#EF5A1E] rounded-full px-3 py-1">{post.category[L]}</span>
              <span className="font-body text-xs text-white/70 flex items-center gap-1"><Clock size={13} /> {post.readTime} {t("read_time")}</span>
              <span className="font-body text-xs text-white/70">{fmt(post.date)}</span>
            </div>
            <h1 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">{post.title[L]}</h1>
          </div>
        </div>
      </div>

      {/* Body */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <p className="font-body text-xl text-[#0B1533] leading-relaxed font-medium mb-8">{post.excerpt[L]}</p>
        <div className="space-y-6">
          {post.body[L].map((item, i) =>
            typeof item === "string" ? (
              <p key={i} className="font-body text-[#3A3F55] text-lg leading-relaxed">{item}</p>
            ) : (
              <h2 key={i} className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0B1533] pt-6 leading-tight">{item.h}</h2>
            )
          )}
        </div>

        {post.faq?.[L]?.length > 0 && (
          <div className="mt-14" data-testid="blog-faq">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-[#0B1533] mb-6">{lang === "de" ? "Häufige Fragen" : "Frequently asked questions"}</h2>
            <div className="space-y-4">
              {post.faq[L].map((f, i) => (
                <details key={i} className="group rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`blog-faq-item-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer font-heading font-semibold text-lg text-[#0B1533] list-none">
                    <span className="pr-4">{f.q}</span>
                    <span className="text-[#EF5A1E] transition-transform duration-200 group-open:rotate-45 text-2xl leading-none shrink-0">+</span>
                  </summary>
                  <p className="font-body text-[#5A607A] text-base leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 rounded-3xl bg-[#F5F2ED] p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div>
            <h3 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533]">{t("hero_title")}</h3>
            <p className="font-body text-[#5A607A] mt-2">{t("cta_sub")}</p>
          </div>
          <Link to="/" onClick={() => window.scrollTo(0, 0)} className="shrink-0 inline-flex items-center gap-2 bg-[#EF5A1E] text-white font-body font-semibold rounded-full px-7 py-3.5 hover:bg-[#D2450F] transition-colors duration-200">
            {t("hero_book_now")} <ArrowRight size={16} />
          </Link>
        </div>
      </article>

      {/* More */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-6">{t("back_to_blog")}</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {more.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="group flex flex-col rounded-3xl overflow-hidden border border-[#E9E5DE] bg-white">
              <div className="h-40 overflow-hidden"><img src={p.image} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" /></div>
              <div className="p-5">
                <h3 className="font-heading font-semibold text-lg tracking-tight text-[#0B1533] leading-snug">{p.title[lang] || p.title.en}</h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
